// External libs
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faEllipsisVertical,
  faSpinner,
  faExclamationTriangle,
  faArrowUp,
  faArrowDown,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

// Components
import DialogComponent from "./DialogComponent.js";

import { renderShortId } from "../utils/utils.js";

/**
 * WidgetTable component displays a table of devices with sorting, filtering, and selection functionalities.
 * It also supports firmware updates and device-specific actions via a dialog.
 *
 * @component
 * @param {Object[]} devices - List of device objects.
 * @param {string} selectedDeviceId - ID of the currently selected device.
 * @param {Function} handleDeviceSelection - Callback to handle device selection.
 * @param {Function} onDialogClick - Callback for dialog interactions.
 * @param {Function} toggleDialog - Function to toggle dialogs.
 * @param {string} showDialogId - ID of the currently displayed dialog.
 */
const WidgetTable = ({
  devices,
  selectedDeviceId,
  handleDeviceSelection,
  onDialogClick,
  toggleDialog,
  showDialogId,
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [filteredData, setFilteredData] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [allowFwUpdate, setAllowFwUpdate] = useState(false);
  const [tableScroll, setTableScroll] = useState(0);
  const [fields, setFields] = useState([
    {
      sortKey: "name",
      displayName: "Name",
      optional: false,
      filterValue: "",
      filterType: "string",
    },
    {
      sortKey: "id",
      displayName: "ID",
      optional: false,
      filterValue: "",
      filterType: "string",
    },
    {
      sortKey: "status",
      displayName: "Status",
      optional: false,
      filterValue: "",
      filterType: "dropdown",
      filterPlaceholder: "Status filter...",
    },
    {
      sortKey: "deviceType",
      displayName: "Device Type",
      optional: true,
      filterValue: "",
      filterType: "dropdown",
      filterPlaceholder: "Type filter...",
    },
    {
      sortKey: "firmware",
      displayName: "Firmware",
      optional: true,
      filterValue: "",
      filterType: "string",
    },
    {
      sortKey: "hardware",
      displayName: "Hardware",
      optional: true,
      filterValue: "",
      filterType: "string",
    },
    {
      sortKey: "tenantName",
      displayName: "Tenant Name",
      optional: true,
      filterValue: "",
      filterType: "dropdown",
      filterPlaceholder: "Tenant filter...",
    },
  ]);

    /* Use effect functions */
    useEffect(() => {
      const update =
        areAllCheckedDevicesCompatible("type") && areAllDevicesOnline();
      setAllowFwUpdate(update);
    }, [selectedDevices]);
  
    useEffect(() => {
      if (!devices)
      {
        return;
      }
      const filteredDevices = devices.filter((device) => {
        let includeDevice = true;
        fields.forEach((field) => {
          if (field.filterValue === "") {
          } else {
            // sortKey return from backend could be null (Device not reported yet)
            const value = String(device[field.sortKey] ?? "").toLowerCase();
            const filter = String(field.filterValue ?? "").toLowerCase();
            if (!value.includes(filter)) {
              includeDevice = false;
            }
          }
        });
        return includeDevice;
      });
      setFilteredData(filteredDevices);
    }, [fields, devices]);

  /**
   * Sorts the filtered data based on the selected sort field and order.
   *
   * @returns {number} Sorting result.
   */
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortField) return 0;
    // value could be NULL
    const aValue = a[sortField] ?? "";
    const bValue = b[sortField] ?? "";
    return sortOrder === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  /**
   * Renders a status indicator icon based on the device status.
   *
   * @param {string} status - Device status.
   * @returns {JSX.Element} Status indicator.
   */
  const renderStatusDot = (status) => {
    let icon;
    if (status.toLowerCase().includes("failed")) {
      icon = faExclamationTriangle;
    } else {
      switch (status.toLowerCase()) {
        case "offline":
        case "online":
          icon = faCircle;
          break;
        case "updating":
        case "rebooting":
          icon = faSpinner;
          break;
        default:
          icon = faCircle;
      }
    }

    const className = `stat-${status.toLowerCase()}`;
    const borderClassName = `status status-${status.toLowerCase()}`;
    const textClassName = `stat-text-${status.toLowerCase()}`;

    return (
      <span className={`${borderClassName}`}>
        <span className={`${className}`}>
          <FontAwesomeIcon icon={icon} size="2xs" spin={icon === faSpinner} />
        </span>
        <span className={`table-text ${textClassName}`}> {status}</span>
      </span>
    );
  };

  /**
   * Sets the filter value of the object in the fields const
   * @param {string} value text for the filter
   * @param {int} index array index value in the fields const
   */
  const setFilterValues = (value, index) => {
    let newFields = [...fields];
    newFields[index].filterValue = value;
    setFields(newFields);
  };

  /**
   * Gets the unique values in the relating column to be displayed in the
   * dropdown filter list
   * @param {string} field field in the fields / table column
   * @returns list of values in the column for the dropdown
   */
  const getDropdownValues = (field) => {
    let values = [];
    if (!devices)
    {
      return values;
    }
    devices.forEach((device) => {
      if (!values.includes(device[field.sortKey])) {
        values.push(device[field.sortKey]);
      }
    });
    return values;
  };

  /**
   * Handles the selection of an individual device checkbox.
   * Unchecks the "select all" checkbox and updates the selected devices list.
   *
   * @param {Event} e - The event object from the checkbox click.
   * @param {Object} device - The device associated with the checkbox.
   */
  const handleDeviceCheckbox = (e, device) => {
    const checkbox_classname = `table-checkbox-all`;
    var checkbox = document.getElementById(checkbox_classname);
    checkbox.checked = false;
    setSelectedDevices(getAllDevicesChecked());
  };

  /**
   * Handles the "select all" checkbox functionality.
   * Toggles all device checkboxes based on the main checkbox state and updates the selected devices list.
   *
   * @param {Event} e - The event object from the checkbox click.
   */
  const handleDeviceAllCheckboxes = (e) => {
    sortedData.forEach((device) => {
      const checkbox_classname = `table-checkbox-${device.id}`;
      var checkbox = document.getElementById(checkbox_classname);
      checkbox.checked = e.target.checked;
    });
    setSelectedDevices(getAllDevicesChecked());
  };

  /**
   * Retrieves a list of all checked devices.
   * Iterates through the sorted data, checking which devices have their checkboxes selected,
   * and returns an array of the checked devices.
   *
   * @returns {Array<Object>} An array of checked device objects.
   */
  const getAllDevicesChecked = () => {
    let newDevices = [];
    sortedData.forEach((device) => {
      const checkbox_classname = `table-checkbox-${device.id}`;
      var checkbox = document.getElementById(checkbox_classname);
      let newDevice = device;
      newDevice.checked = checkbox.checked;
      if (newDevice.checked) {
        newDevices.push(newDevice);
      }
    });
    return newDevices;
  };

  /**
   * Checks if all selected devices have the same value for a given field.
   *
   * @param {string} field - The field to compare across selected devices.
   * @returns {boolean} True if all checked devices have the same value for the specified field, false otherwise.
   */
  const areAllCheckedDevicesCompatible = (field) => {
    const checkedDevices = selectedDevices;
    if (checkedDevices.length === 0) {
      return false;
    }
    const devices = checkedDevices.map((device) => device[field]);
    return devices.every((val, i, arr) => val === arr[0]);
  };

  /**
   * Checks if all selected devices are online.
   *
   * @returns {boolean} True if all checked devices have a status of "Online", false otherwise.
   */
  const areAllDevicesOnline = () => {
    const checkedDevices = selectedDevices;
    if (checkedDevices.length === 0) {
      return false;
    }
    const devices = checkedDevices.map((device) => device["status"]);
    return devices.every((val, i, arr) => val === "Online");
  };

  /**
   * Handles the scroll of the table and sets the table scroll state which
   * is used for setting the y offset of the dialog components in the table
   * @param {Event} e scroll event
   */
  const handleScroll = (e) => {
    setTableScroll(e.target.scrollTop);
  };

  /**
   * Generates and returns the html for the filter rows basesd on the fields
   * const variable
   * @returns the html for the filter rows
   */
  function getFilterHtml() {
    return fields.map((field, index) => {
      let className = "table-filter-input";
      if (field.optional) {
        className += " optional-column";
      }
      if (field.filterType === "string") {
        return (
          <td>
            <input
              type="text"
              className={className}
              placeholder={`Filter ${field.displayName.toLowerCase()}`}
              value={fields[index].filterValue}
              onChange={(e) => setFilterValues(e.target.value, index)}
            />
          </td>
        );
      } else if (field.filterType === "dropdown") {
        if (field.filterValue === "") {
          className += " table-filter-input-placeholder";
        }
        return (
          <td>
            <select
              className={className}
              value={fields[index].filterValue}
              onChange={(e) => setFilterValues(e.target.value, index)}
            >
              <option className="table-filter-input-placeholder" value="">
                {field.filterPlaceholder}
              </option>
              {getDropdownValues(field).map((value) => (
                <option value={value}>{value}</option>
              ))}
            </select>
          </td>
        );
      }
    });
  }

  return (
    <div class="data-table-div" onScroll={handleScroll}>
      <table class="data-table">
        <thead>
          <tr>
            <th className="icon-table-th">
              <input
                type="checkbox"
                className="table-checkbox"
                id="table-checkbox-all"
                onChange={(e) => handleDeviceAllCheckboxes(e)}
              />
            </th>
            {fields.map((field) => (
              <th
                class={
                  field.optional === true
                    ? "data-table-sortable-header optional-column"
                    : "data-table-sortable-header"
                }
                onClick={() => {
                  setSortField(field.sortKey);
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                }}
              >
                {field.displayName}{" "}
                {sortField === field.sortKey ? (
                  sortOrder === "asc" ? (
                    <FontAwesomeIcon icon={faArrowUp} />
                  ) : (
                    <FontAwesomeIcon icon={faArrowDown} />
                  )
                ) : (
                  ""
                )}
              </th>
            ))}
            <th className="icon-table-th" h>
              <button
                class="expand-btn icon-btn"
                key={"all"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDialog("all");
                }}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>
            </th>
          </tr>
        </thead>
        <tr>
          <td></td>
          {getFilterHtml()}
          <td>
            {showDialogId == "all" && (
              <DialogComponent
                key={`dialog-${showDialogId}-${allowFwUpdate}`}
                onButtonClick={onDialogClick}
                devices={selectedDevices}
                disableEdit={true}
                disableFirmware={!allowFwUpdate}
                toggleShow={showDialogId == "all"}
                id={"all"}
              />
            )}
          </td>
        </tr>
        {sortedData.map((device) => (
          <tr className={device.id === selectedDeviceId ? "data-table-row data-table-row-selected" : "data-table-row"}>
            <td>
              <input
                type="checkbox"
                className="table-checkbox"
                id={`table-checkbox-${device.id}`}
                checked={selectedDevices.some((d) => d.id === device.id)}
                onChange={(e) => handleDeviceCheckbox(e, device)}
              />
            </td>
            <td>
              <span
                class="table-text expand-btn"
                id={`table_tooltip_${device.id}`}
                key={device.id}
                onClick={() => handleDeviceSelection(device.id)}
              >
                {device.name}
              </span>

              <div className="table_tooltip">
                <Tooltip
                  anchorSelect={`#table_tooltip_${device.id}`}
                  place="bottom-start"
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{`Device: ${device.name}`}</span>
                    <span>{`ID: ${device.id}`}</span>
                    <span>{`Firmware: ${device.firmware}`}</span>
                    <span>{`Hardware: ${device.hardware}`}</span>
                    <span>{`Tenant Name: ${device.tenantName}`}</span>
                  </div>
                </Tooltip>
              </div>
            </td>
            <td>
              <span class="table-text monospace">
                {renderShortId(device.id)}
              </span>
            </td>
            <td>{renderStatusDot(device.status)}</td>
            <td class="optional-column">
              <span class="table-text">{device.deviceType}</span>
            </td>
            <td class="optional-column">
              <span class="table-text">{device.firmware}</span>
            </td>
            <td class="optional-column">
              <span class="table-text">{device.hardware}</span>
            </td>
            <td class="optional-column">
              <span class="table-text">{device.tenantName}</span>
            </td>
            <td>
              <button
                class="expand-btn icon-btn"
                key={device.id}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDialog(device.id);
                }}
              >
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>
              <DialogComponent
                onButtonClick={onDialogClick}
                devices={[device]}
                disableIdentify={device.status == "Online" ? false : true}
                disableFirmware={device.status == "Online" ? false : true}
                toggleShow={showDialogId === device.id}
                id={device.id}
                translateY={tableScroll}
              />
            </td>
          </tr>
        ))}
      </table>
    </div>
  );
};

WidgetTable.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedDeviceId: PropTypes.string.isRequired,
  handleDeviceSelection: PropTypes.func.isRequired,
  onDialogClick: PropTypes.func.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  showDialogId: PropTypes.string.isRequired,
};

export default WidgetTable;
